import React, { useState, useEffect } from 'react';
import { Bookmark, Check } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { useAnalytics } from '../analytics';
import { useNavigate } from 'react-router-dom';
import { useToast } from '../context/ToastContext';
import { useWichList } from '../hooks/useWichList';

/**
 * WantToTryButton - Dedicated button for adding/removing sandwiches to WichList (formerly Want to Try)
 * 
 * Features:
 * - Optimistic UI updates with revert on failure
 * - Accessible labels and keyboard navigation
 * - GA event tracking
 * - Handles logged-out state
 * 
 * @param {string} sandwichId - The sandwich ID
 * @param {boolean} initialActive - Initial state (is sandwich in Want to Try?)
 * @param {number} size - Icon size for compact variant (default: 20)
 * @param {string} label - Optional custom label (compact variant only)
 * @param {('compact'|'prominent')} variant - Visual style; 'prominent' shows colorful wordmark pill
 * @param {string} source - Analytics source (feed, detail, search, profile)
 * @param {function} onToggle - Optional callback after successful toggle
 */
const WantToTryButton = ({
  sandwichId,
  initialActive,
  size = 20,
  label,
  variant = 'compact',
  source = 'unknown',
  sourceReviewId = null,
  onToggle,
  onFeedback
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const { user } = useAuth();
  const { trackEvent } = useAnalytics();
  const navigate = useNavigate();
  const toast = useToast();
  const { isInWichList, toggleWichList, primeMembership } = useWichList();

  const isActive = isInWichList(sandwichId);

  // Seed shared membership map from any known initialActive value (for legacy callers
  // that still compute membership themselves).
  useEffect(() => {
    if (initialActive === undefined || initialActive === null) {
      return;
    }
    primeMembership(sandwichId, initialActive);
  }, [initialActive, primeMembership, sandwichId]);

  const handleClick = async (e) => {
    e.preventDefault();
    e.stopPropagation();

    // Redirect to login if not authenticated
    if (!user) {
      navigate('/login');
      return;
    }

    setIsLoading(true);

    try {
      const nextState = await toggleWichList(sandwichId, { sourceReviewId, source });
      
      trackEvent(nextState ? 'want_to_try_add' : 'want_to_try_remove', {
        sandwich_id: sandwichId,
        source,
        copy_variant: 'wichlist'
      });

      // Callback for parent component updates
      if (onToggle) {
        onToggle(nextState);
      }

      if (onFeedback) {
        onFeedback(nextState);
      } else if (toast?.show) {
        toast.show({
          message: nextState ? 'Added to your WichList' : 'Removed from your WichList',
          variant: nextState ? 'success' : 'default'
        });
      }
    } catch (error) {
      console.error('Failed to toggle WichList:', error);
      if (toast?.show) {
        toast.show({
          message: 'We couldn’t update your WichList. Please try again.',
          variant: 'error'
        });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const ariaLabel = label || (isActive ? 'Remove from WichList' : 'Add to WichList');

  if (variant === 'prominent') {
    return (
      <button
        onClick={handleClick}
        disabled={isLoading}
        className={`want-to-try-badge ${isActive ? 'active' : ''} ${
          isLoading ? 'opacity-70 cursor-not-allowed' : ''
        }`}
        aria-label={ariaLabel}
        title={ariaLabel}
        aria-pressed={isActive ? 'true' : 'false'}
      >
        {isActive ? (
          <Check size={14} aria-hidden="true" />
        ) : (
          <Bookmark size={14} aria-hidden="true" />
        )}
        <span>{isActive ? 'WichList' : 'Add to WichList'}</span>
      </button>
    );
  }

  return (
    <button
      onClick={handleClick}
      disabled={isLoading}
      className={`want-to-try-compact flex items-center space-x-1 transition-colors ${
        isActive
          ? 'text-passion-red hover:text-red-700'
          : 'text-gray-400 hover:text-passion-red'
      } ${isLoading ? 'opacity-50 cursor-not-allowed' : ''}`}
      aria-label={ariaLabel}
      title={ariaLabel}
      aria-pressed={isActive ? 'true' : 'false'}
    >
      {isActive ? (
        <Check size={size} className="want-to-try-icon" aria-hidden="true" />
      ) : (
        <Bookmark size={size} className="want-to-try-icon" fill={'none'} aria-hidden="true" />
      )}
      {label && <span className="want-to-try-label text-sm">{label}</span>}
    </button>
  );
};

export default WantToTryButton;


